import type {RenderDataObject, RendererImage, RendererInfo, RendererStatus, RenderMetadata, RenderNodes} from "@cm/render-nodes"
import type {CmLogger} from "@cm/log"
import {RemoteMessagingConnection} from "@cm/utils/websocket/remote-messaging"
import type {WebSocketImplementation} from "@cm/utils/websocket/types"
import type {CreateWebSocketMessagingConnection, WebSocketMessagingConnection} from "@cm/utils/websocket/websocket-messaging"
import {firstValueFrom} from "rxjs"
import {graphToJson} from "@cm/utils/graph-json"

export type CacheableEntity = RenderDataObject

export type InvalidatableEntity = Pick<RenderDataObject, "type" | "dataObjectId">

export const connectRenderEngineMessaging =
    <RuntimeImplementation extends WebSocketImplementation>(
        createWebSocketMessagingConnection: CreateWebSocketMessagingConnection<RuntimeImplementation>,
        logger: CmLogger,
    ) =>
    (url: string, maxPayload?: number) => {
        return firstValueFrom(createWebSocketMessagingConnection(url, maxPayload)).then((messaging: WebSocketMessagingConnection<RuntimeImplementation>) => {
            return new RenderEngineMessagingConnection(messaging, logger)
        })
    }

export class RenderEngineMessagingConnection<RuntimeImplementation extends WebSocketImplementation> extends RemoteMessagingConnection<RuntimeImplementation> {
    public constructor(
        messaging: WebSocketMessagingConnection<RuntimeImplementation>,
        protected override logger: CmLogger,
    ) {
        super(messaging, "Remote renderer", logger)
    }

    getInfo(): Promise<RendererInfo> {
        return this.doTransaction({command: "getInfo"})
    }

    async update(graph: RenderNodes.Render) {
        await this.doTransaction({command: "update", graph: graphToJson(graph, this.logger)})
    }

    async stop(): Promise<void> {
        await this.doTransaction({command: "stop"})
    }

    async cacheEntity(entity: CacheableEntity) {
        const {type, dataObjectId, contentType} = entity
        const source = "localFile" in entity ? {localPath: entity.localFile.path, localPathOwnership: entity.localFile.ownership} : {data: entity.data}
        await this.doTransaction({command: "cacheEntity", entity: {type, dataObjectId, contentType, ...source}})
    }

    async invalidateEntity(entity: InvalidatableEntity) {
        await this.doTransaction({command: "invalidateEntity", entity})
    }

    async clearCaches() {
        await this.doTransaction({command: "clearCaches"})
    }

    getStatus(): Promise<RendererStatus> {
        return this.doTransaction({command: "getStatus"})
    }

    getRenderMetadata(): Promise<RenderMetadata> {
        return this.doTransaction({command: "getRenderMetadata"})
    }

    getPreviewImage(format: "raw" | "exr16"): Promise<RendererImage> {
        return this.doTransaction({command: "getPreviewImage", format: format})
    }

    getFinalImage(passName: string, format: "raw" | "exr16" | "exr32"): Promise<RendererImage> {
        return this.doTransaction({command: "getFinalImage", pass: passName, format})
    }
}
